import React from "react";
import type { FieldValues, UseFormReturn } from "react-hook-form";
import { DeleteTravelerDialog } from "@apac/checkout";
import {
  AdditionalInfo,
  type AdditionalInfoFieldOverrides,
  FrequentFlyerDetails,
  type FrequentFlyerFieldOverrides,
  PassportDetails,
  type PassportDetailsFieldOverrides,
  TravelerEditMode,
  ApacTravelersComponent,
  APAC_TRAVELERS_NAME,
} from "@apac/travelers";
import { useModuleBEM, useStyles } from "@b2bportal/core-themes";
import { useI18nContext } from "@hopper-b2b/i18n";
import { Button, ButtonTypeEnum } from "@tenant/ui";
import {
  getLocalizedDateFormat,
  getLocalizedDateFormatter,
} from "../dateFormatConfig";
import { DriverLicense } from "../DriverLicense";
import { rbcFlyerOptions } from "../frequentFlyerOptions";
import { TravelerDetails } from "../TravelerDetails";
import {
  useTravellerForm,
  type UseTravellerFormParams,
} from "../useTravellerForm";
import defaultStyles from "./TravellerForm.module.scss";
import {
  ActivityDetails,
  type ActivityDetailsFieldOverrides,
  type ActivityFieldValues,
} from "@/modules/activities-checkout/components/ActivityDetails";

type FormContext = UseFormReturn<FieldValues, unknown, undefined>;

export type TravellerFormSections = {
  travellerDetails?: Record<string, unknown>;
  additionalInfo?: AdditionalInfoFieldOverrides;
  passportDetails?: PassportDetailsFieldOverrides;
  frequentFlyer?: FrequentFlyerFieldOverrides;
  driverLicense?: Record<string, unknown>;
  activityDetails?: ActivityDetailsFieldOverrides;
};

export type NameFieldsType = "firstName" | "middleName" | "lastName";

interface TravellerFormProps extends UseTravellerFormParams {
  onClose: () => void;
  onDelete: (id: string) => void;
  dateFormat?: string;
  showPassportDetails?: boolean;
  fieldOverrides?: TravellerFormSections;
  subtitle?: string;
  showGender?: boolean;
  travelerEditMode?: TravelerEditMode;
  missingDriverInfoError?: boolean;
  missingPassportInfoError?: boolean;
  nameFieldsOrder?: NameFieldsType[];
  closeButtonText?: string;
  hideFlightsInfo?: boolean;
  hideDriverInfo?: boolean;
  travelerBookingQuestions?: Array<{
    id: string;
    label: string;
    required: string;
  }>;
  savedActivityFieldValues?: ActivityFieldValues;
}

export const TravellerForm: React.FC<TravellerFormProps> = (props) => {
  const { t, language } = useI18nContext();
  const styles = useStyles(
    APAC_TRAVELERS_NAME,
    ApacTravelersComponent.TravellerForm,
    defaultStyles
  );
  const [block, cn] = useModuleBEM(
    styles,
    ApacTravelersComponent.TravellerForm
  );

  const {
    onClose,
    onDelete,
    traveler,
    fieldOverrides = {} as TravellerFormSections,
    showPassportDetails,
    travelerEditMode,
    missingDriverInfoError = false,
    missingPassportInfoError = false,
    closeButtonText,
    hideDriverInfo = false,
    hideFlightsInfo = false,
    travelerBookingQuestions,
    savedActivityFieldValues,
  } = props;

  const {
    additionalInfo = {},
    passportDetails = {},
    frequentFlyer = {},
    driverLicense = {},
    activityDetails = {},
  } = fieldOverrides;

  const localizedDateFormat = getLocalizedDateFormat(language);
  const localizedDateFormatter = getLocalizedDateFormatter(language);

  const {
    context: { formContext, existingPassenger, frequentFlyerList },
    handlers: {
      onFrequentFlyerSubmit,
      onRemoveFrequentFlyer,
      onSubmitTraveller,
    },
  } = useTravellerForm({
    ...props,
    dateFormat: localizedDateFormat,
  });

  const showFlightsInfo =
    !hideFlightsInfo &&
    (travelerEditMode === TravelerEditMode.FLIGHTS_ONLY ||
      travelerEditMode === TravelerEditMode.ALL);

  const showDriverInfo =
    !hideDriverInfo &&
    (travelerEditMode === TravelerEditMode.CARS_ONLY ||
      travelerEditMode === TravelerEditMode.ALL);

  const showActivitiesInfo =
    travelerEditMode === TravelerEditMode.ACTIVITIES_ONLY;

  const showNationality = travelerEditMode !== TravelerEditMode.HOTELS_ONLY;

  const passportDetailsFieldOverrides = {
    ...passportDetails,
    nationality: {
      ...passportDetails.nationality,
      placeholder: t("choose"),
    },
    passportIssuanceCountry: {
      ...passportDetails.passportIssuanceCountry,
      placeholder: t("choose"),
    },
    passportNumber: {
      ...passportDetails.passportNumber,
      placeholder: t("travelerForm.passportDetails.passportNumberPlaceholder"),
    },
    passportExpiryDate: {
      ...passportDetails.passportExpiryDate,
      placeholder: t(
        "travelerForm.passportDetails.passportExpiryDatePlaceholder"
      ),
      dateFormatter: localizedDateFormatter,
    },
  };

  return (
    <form className={block} onSubmit={onSubmitTraveller}>
      <TravelerDetails
        formContext={formContext as unknown as FormContext}
        showNationality={showNationality}
        dateFormatter={localizedDateFormatter}
      />

      {showFlightsInfo && (
        <>
          <FrequentFlyerDetails
            fieldOverrides={{
              ...frequentFlyer,
              frequentFlyerProgram: {
                ...frequentFlyer.frequentFlyerProgram,
                options: rbcFlyerOptions,
                placeholder: t("choose"),
              },
              frequentFlyerNumber: {
                ...frequentFlyer.frequentFlyerNumber,
                placeholder: t(
                  "travelerForm.frequentFlyer.frequentFlyerNumberPlaceholder"
                ),
              },
            }}
            formContext={formContext as unknown as FormContext}
            frequentFlyerList={frequentFlyerList}
            onFrequentFlyerSubmit={onFrequentFlyerSubmit}
            onRemoveFrequentFlyer={onRemoveFrequentFlyer}
            subtitle={t("travelerForm.frequentFlyer.subtitle")}
          />

          {showPassportDetails && (
            <PassportDetails
              fieldOverrides={passportDetailsFieldOverrides}
              formContext={formContext as unknown as FormContext}
              missingInfoError={missingPassportInfoError}
            />
          )}

          <AdditionalInfo
            fieldOverrides={{
              ...additionalInfo,
              redressNumber: {
                ...additionalInfo.redressNumber,
                placeholder: t(
                  "travelerForm.additionalInfo.redressNumberPlaceholder"
                ),
              },
              travellerNumber: {
                ...additionalInfo.travellerNumber,
                placeholder: t(
                  "travelerForm.additionalInfo.knownTravelerNumberPlaceholder"
                ),
              },
            }}
            formContext={formContext as unknown as FormContext}
          />
        </>
      )}

      {showActivitiesInfo && (
        <>
          {showPassportDetails && (
            <PassportDetails
              fieldOverrides={passportDetailsFieldOverrides}
              formContext={formContext as unknown as FormContext}
              missingInfoError={missingPassportInfoError}
            />
          )}

          <ActivityDetails
            fieldOverrides={activityDetails}
            formContext={formContext as unknown as FormContext}
            bookingQuestions={travelerBookingQuestions}
            savedActivityFieldValues={savedActivityFieldValues}
          />
        </>
      )}

      {showDriverInfo && (
        <DriverLicense
          fieldOverrides={driverLicense}
          formContext={formContext as unknown as FormContext}
          missingInfoError={missingDriverInfoError}
        />
      )}

      <div className={cn("button-group")}>
        <Button
          className={cn("submit-button")}
          buttonType={ButtonTypeEnum.Primary}
          type="submit"
        >
          {t("saveTraveler")}
        </Button>

        {existingPassenger && (
          <DeleteTravelerDialog
            text={{}}
            onConfirmDelete={() => {
              traveler && onDelete(traveler.id);
              onClose();
            }}
            trigger={
              <Button
                className={cn("remove-button")}
                buttonType={ButtonTypeEnum.Primary}
              >
                {t("delete")}
              </Button>
            }
          />
        )}

        <Button
          className={cn("close-button")}
          buttonType={ButtonTypeEnum.Tertiary}
          type="button"
          onClick={onClose}
        >
          {closeButtonText ?? t("close.button")}
        </Button>
      </div>
    </form>
  );
};
